import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { setCurrencyCode, setDisplayTimeZone } from '@/shared/lib/format'
import { AuthContext, type AuthContextValue } from './AuthContext'
import {
  chooseTenant,
  confirmTotp,
  fetchMe,
  login,
  logout,
  switchTenant as switchTenantRequest,
  verifyTotp,
  type CurrentUser,
  type LoginOutcome,
  type LoginSuccess,
  type TotpOutcome,
} from './authApi'
import type { Permission } from './permissions'
import { clearSession, hasValidToken, saveSession } from './tokenStorage'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  // Token localStorage'da, lekin React uni "ko'rmaydi" — shuning uchun
  // holatga nusxalanadi. Aks holda kirgandan keyin daraxt qayta chizilmaydi.
  const [hasToken, setHasToken] = useState(hasValidToken)

  const meQuery = useQuery<CurrentUser>({
    queryKey: queryKeys.me,
    queryFn: fetchMe,
    enabled: hasToken,
    // Kim ekanimiz sessiya davomida o'zgarmaydi; rol o'zgarsa qayta kirish kerak
    staleTime: Infinity,
    retry: false,
  })

  const user = meQuery.data ?? null

  /**
   * TENANT VALYUTASI SHU YERDA O'RNATILADI.
   *
   * `formatMoney` valyutani modul darajasidagi o'zgaruvchidan o'qiydi
   * (`shared/lib/format.ts`) — u React holati emas va REAKTIV EMAS. Agar bu
   * qiymat ilova daraxtining pastida o'rnatilsa, uni so'ramagan sahifalarda
   * summalar ZAXIRA valyutada chiqadi. MVP'da aynan shunday bo'lgan edi:
   * sozlamalarda AFN tanlangan, lekin boshqa sahifa qayta yuklangach yana
   * so'mda ko'rinardi.
   *
   * Yechim ikki qismdan iborat va IKKALASI ham kerak:
   *   1. qiymat ILDIZDA, `me` javobidan o'rnatiladi (shu yer)
   *   2. `me` yuklanmaguncha himoyalangan sahifalar CHIZILMAYDI
   *      (`RequireAuth`) — aks holda summalar avval noto'g'ri chiqib,
   *      keyin sakrab o'zgaradi
   *
   * NEGA `useEffect` EMAS: effekt birinchi chizilishdan KEYIN ishlaydi, ya'ni
   * bolalar bir marta eski valyuta bilan chizilib ulgurardi. Bu yerda esa
   * qiymat bolalar chizilishidan oldin o'rnatiladi. Amal idempotent —
   * qayta chizilishda takrorlanishi zararsiz.
   *
   * ⚠️ `currency` `null` BO'LISHI MUMKIN: backend uni bermaydi
   * (`tenantPrefs.ts` dagi izoh) va operator hali tanlamagan bo'lishi mumkin.
   * `setCurrencyCode` bo'sh qiymatda hech narsa qilmaydi, ya'ni oldingi
   * (zaxira) kod saqlanadi — summa hech qachon birliksiz qolmaydi.
   */
  /**
   * TENANT VAQT ZONASI ham AYNAN SHU YERDA — valyuta bilan bir xil sabab.
   *
   * `formatDateTime` zonani modul darajasidagi o'zgaruvchidan o'qiydi, u
   * REAKTIV EMAS. `useEffect` da o'rnatilsa, effekt birinchi chizilishdan
   * KEYIN ishlaydi — ya'ni butun daraxt bir marta BRAUZER zonasida chizilib
   * ulgurardi. Toshkentdagi va Kobuldagi xodim uchun bu bir xil sessiyaning
   * ikki xil soati demakdir, ustiga kun chegarasi (hisobotlar) tenant
   * zonasida hisoblangani uchun sanalar ham mos kelmasdi.
   *
   * ⚠️ `timezone` `null` bo'lishi mumkin (eski backend). `setDisplayTimeZone`
   * bo'sh yoki noto'g'ri qiymatda hech narsa qilmaydi — u holda brauzer
   * zonasi ishlatiladi, ya'ni vaqt hech qachon yo'qolmaydi.
   */
  if (user) {
    setCurrencyCode(user.tenant.currency)
    setDisplayTimeZone(user.tenant.timezone)
  }

  /**
   * Sessiyani yakunlaydi — kirish oqimining qaysi yo'lidan yakuniy tokenga
   * yetib kelinsa ham (tashkilot tanlash, TOTP sozlash, TOTP tekshiruv)
   * oxirida shu bir joyga kelib qo'shiladi.
   *
   * ⚠️ REFRESH TOKENI HAM SHU YERDA SAQLANADI. Backend uni har yakuniy
   * javobda beradi (kirish ham, tashkilot almashtirish ham), lekin panel
   * ilgari uni tashlab yuborardi — natijada 14 kunlik sessiya 30 daqiqada
   * tugardi. Yagona yozish nuqtasi bo'lgani uchun uni bu yerda ulash
   * BARCHA yo'llarni qamrab oladi.
   */
  const finishLogin = useCallback(
    (result: LoginSuccess) => {
      saveSession(result.token, result.expiresAt, result.refreshToken)
      setHasToken(true)
      // Kirish javobi `me` ni ALLAQACHON o'z ichiga oladi — uni keshga
      // qo'yamiz va `/api/me` ni qayta so'ramaymiz. Eski foydalanuvchining
      // keshi ham shu bilan almashadi.
      queryClient.setQueryData(queryKeys.me, result.user)
    },
    [queryClient],
  )

  // Login endi hech qachon shu yerda tugamaydi (TOTP majburiy), shuning
  // uchun natija o'zgarishsiz chaqiruvchiga (`LoginPage`) qaytariladi — u
  // TOTP ekraniga yo'naltiradi.
  const signIn = useCallback(async (username: string, password: string): Promise<LoginOutcome> => {
    return login({ username, password })
  }, [])

  const completeTenantChoice = useCallback(
    async (choiceToken: string, tenantId: string) => {
      finishLogin(await chooseTenant(choiceToken, tenantId))
    },
    [finishLogin],
  )

  /**
   * TOTP javobini yakunlaydi — natija UCH XIL bo'lishi mumkin:
   * bitta a'zolik bo'lsa sessiya shu yerda saqlanadi, ko'p a'zolik bo'lsa
   * yoki a'zolik umuman yo'q bo'lsa sessiya SAQLANMAYDI va natija
   * chaqiruvchiga (`TotpPage`) qaytariladi — u tegishli ekranga o'tadi.
   */
  const finishTotp = useCallback(
    (outcome: TotpOutcome): TotpOutcome => {
      if (outcome.kind === 'authenticated') finishLogin(outcome)
      return outcome
    },
    [finishLogin],
  )

  const completeTotpSetup = useCallback(
    async (setupToken: string, code: string): Promise<TotpOutcome> => {
      return finishTotp(await confirmTotp(setupToken, code))
    },
    [finishTotp],
  )

  const completeTotpVerify = useCallback(
    async (totpToken: string, code: string): Promise<TotpOutcome> => {
      return finishTotp(await verifyTotp(totpToken, code))
    },
    [finishTotp],
  )

  /**
   * TASHKILOTNI ALMASHTIRISH — panel holatining eng xavfli o'tish nuqtasi.
   *
   * ⚠️ KESH TO'LIQ TOZALANADI va bu shart. Har so'rov kaliti tashkilotni
   * O'Z ICHIGA OLMAYDI (`queryKeys.ts`: `['subscribers','list',params]`) —
   * chunki bitta sessiya bitta tashkilotda bo'ladi degan taxminga qurilgan.
   * Tozalanmasa, o'tgandan keyin abonentlar ro'yxati, hisobotlar va
   * balanslar ekranda ESKI tashkilotning ma'lumoti bilan qolib ketardi va
   * operator buni bilmagan holda boshqa tashkilotning abonentini ko'rib
   * turardi. Bu shunchaki eskirgan ma'lumot emas — bu boshqa tashkilotga
   * tegishli ma'lumot.
   *
   * ⚠️ `cancelQueries` `clear` dan OLDIN. Almashtirish paytida uchib
   * ketayotgan so'rovlar bo'lishi mumkin; ular tozalashdan KEYIN javob
   * qaytarsa, eski tenant ma'lumotini yangi keshga qayta yozardi —
   * ya'ni tozalash bo'lgan, lekin foydasi yo'q.
   *
   * Tartib: so'rovlarni to'xtat → keshni tozala → yangi sessiyani saqla.
   * `finishLogin` oxirida chaqiriladi, chunki u `me` ni keshga yozadi va
   * undan keyin tozalash aynan o'sha yozuvni o'chirib yuborardi.
   */
  const switchTenant = useCallback(
    async (tenantId: string) => {
      const result = await switchTenantRequest(tenantId)
      await queryClient.cancelQueries()
      queryClient.clear()
      finishLogin(result)
    },
    [queryClient, finishLogin],
  )

  const signOut = useCallback(async () => {
    await logout()
    clearSession()
    setHasToken(false)
    queryClient.clear()
  }, [queryClient])

  const can = useCallback(
    (permission: Permission): boolean => user?.permissions.has(permission) ?? false,
    [user],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: hasToken && user !== null,
      isLoading: hasToken && meQuery.isPending,
      error: meQuery.error,
      signIn,
      completeTenantChoice,
      completeTotpSetup,
      completeTotpVerify,
      switchTenant,
      signOut,
      can,
    }),
    [
      user,
      hasToken,
      meQuery.isPending,
      meQuery.error,
      signIn,
      completeTenantChoice,
      completeTotpSetup,
      completeTotpVerify,
      switchTenant,
      signOut,
      can,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
